import { memo, useRef, useEffect, forwardRef, useImperativeHandle, useMemo, CSSProperties } from 'react';
import lottie, { AnimationItem, AnimationConfigWithPath } from 'lottie-web';
import { getQaStore } from '@/sub-luca-plugin/store/qa';
import { getUuid } from '@/sub-luca-plugin/utils';
import purpleBallBouncing from '@/sub-luca-plugin/assets/jsons/purpleBallBouncing.json';
import complexLoading from '@/sub-luca-plugin/assets/jsons/complexLoading.json';
import resolved from '@/sub-luca-plugin/assets/jsons/resolved.json';
import unresolved from '@/sub-luca-plugin/assets/jsons/unresolved.json';
import starsTwinkle from '@/sub-luca-plugin/assets/jsons/starsTwinkle.json';
import collect from '@/sub-luca-plugin/assets/jsons/collect.json';

export type lottieContainerRef = Pick<AnimationItem, 'play' | 'pause'>;

type TAnimationType = 'purpleBallBouncing' | 'complexLoading' | 'resolved' | 'unresolved' | 'starsTwinkle' | 'collect';
interface IProps extends Partial<Pick<AnimationConfigWithPath<'svg'>, 'loop'>> {
    type: TAnimationType;
    width?: number;
    height?: number;
    className?: string;
    style?: CSSProperties;
    /**
     * 延迟
     */
    delay?: number;
    /**
     * 动效完成
     */
    onAnimationFinish?: () => void;
    /**
     * 执行类型
     * @params
     * directCompletion:直接跳到最后一帧
     * directCompletionByInit:luca刚初始化完成时直接跳到最后一帧
     * autoPlayByInit: 初始化的时候自动播放
     */
    excuteType?: 'directCompletion' | 'directCompletionByInit' | 'autoPlayByInit';
}

const LottieContainer = (
    { width, height, type, className, style = {}, loop = false, delay, onAnimationFinish, excuteType = 'directCompletionByInit' }: IProps,
    ref: any
) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const animationItem = useRef<AnimationItem>();

    const animationPath = useRef<Record<TAnimationType, { json: any; duration: number }>>({
        purpleBallBouncing: {
            json: purpleBallBouncing,
            duration: 100,
        },
        complexLoading: {
            json: complexLoading,
            duration: 100,
        },
        resolved: {
            json: resolved,
            duration: 100,
        },
        unresolved: {
            json: unresolved,
            duration: 100,
        },
        starsTwinkle: {
            json: starsTwinkle,
            duration: 100,
        },
        collect: {
            json: collect,
            duration: 45,
        },
    });

    const newStyle = useMemo(() => {
        return {
            width: width ? `${width}px` : undefined,
            height: height ? `${height}px` : undefined,
            ...style,
        };
    }, [width, height]);

    const handleAnimationFinish = () => {
        onAnimationFinish?.();
    };

    useImperativeHandle(
        ref,
        () => ({
            play: () => animationItem.current?.play(),
            goToAndStop: (...args: any) => animationItem.current?.goToAndStop.call(animationItem.current!, args),
        }),
        []
    );

    useEffect(() => {
        const name = getUuid();
        animationItem.current = lottie.loadAnimation({
            container: containerRef.current!,
            name: name,
            renderer: 'svg',
            loop: loop,
            autoplay: false,
            animationData: animationPath.current[type].json,
        });
        animationItem.current.addEventListener('complete', handleAnimationFinish);
        if (excuteType === 'directCompletion') {
            animationItem.current!.goToAndStop(animationPath.current[type].duration, true, name);
        } else if (excuteType === 'directCompletionByInit' && !getQaStore()?.qaConfig.canExcuteAimation) {
            animationItem.current!.goToAndStop(animationPath.current[type].duration, true, name);
        } else if (excuteType === 'autoPlayByInit' && delay) {
            animationItem.current.goToAndStop(0.1, true, name);
            setTimeout(() => animationItem.current?.play(), delay);
        } else if (excuteType === 'autoPlayByInit') {
            animationItem.current.goToAndPlay(0.1, true, name);
        }
        return () => {
            animationItem.current?.removeEventListener('complete', handleAnimationFinish);
            animationItem.current?.destroy(name);
        };
    }, []);

    return <div ref={containerRef} className={className} style={newStyle}></div>;
};

export default memo(forwardRef(LottieContainer));
